import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Flow } from '../components/Diagram';

interface Axis {
  key: string;
  name: string;
  blurb: string;
}

const DURATIONS: Axis[] = [
  { key: 'FIRST_ONLY', name: 'First invoice only', blurb: 'Applies to one billing-cycle invoice, the next one the subscription raises, then expires automatically. Applied while a subscription is being created, that is its first invoice. Typical for a signup incentive.' },
  { key: 'FIXED_COUNT', name: 'Fixed number of renewals', blurb: 'Applies to a set number of billing-cycle invoices (e.g. 3 months off), counting the next invoice as the first, then expires automatically. Mid-cycle adjustment invoices never use up the count. The instance tracks appliedCount against the count.' },
  { key: 'FOREVER', name: 'Forever', blurb: 'Applies to every billing-cycle invoice until someone cancels it. Used for negotiated, ongoing rate reductions.' },
];

const SOURCES: Axis[] = [
  { key: 'LIBRARY', name: 'From the library', blurb: 'Pick an existing Discount Definition. The instance inherits its type, value, and duration as a snapshot — later edits to the definition don’t reach back into deals already applied.' },
  { key: 'ONE_OFF', name: 'One-off', blurb: 'Define the type, value, and duration inline, scoped to this one subscription only. Nothing is written to the library, and no other subscription can reuse it — the terms live only on this instance.' },
];

const STACKING: Axis[] = [
  { key: 'COMBINE', name: 'Combine (default)', blurb: 'All active discounts apply together. Fixed-amount discounts are subtracted first, then percentage discounts are applied to what remains.' },
  { key: 'BEST_WINS', name: 'Best wins', blurb: 'Only the single discount that saves the customer the most is applied, compared on each invoice; the rest sit idle while active. A tie goes to the one applied earliest.' },
  { key: 'FIRST_WINS', name: 'First wins', blurb: 'Only the discount that was applied earliest takes effect.' },
  { key: 'LATEST_WINS', name: 'Latest wins', blurb: 'Only the most recently applied discount takes effect — useful when a new promo should supersede an old one.' },
];

export function DiscountingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Flexible discounting — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Flexible discounting</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Discount <em>after</em> pricing resolves.
        </h1>
        <p className="dp-oneliner">
          A discount never changes the underlying rate. It is a named, time-bound deduction applied
          on top of whatever price the resolver chain already produced &mdash; computed at invoice-line
          time, fully audited, and reversible without touching the base pricing at all.
        </p>
      </header>

      <section className="dp-section">
        <h2>Definitions vs. instances</h2>
        <p className="dp-plain">
          Discounting uses a library/instance split, the same shape as a coupon template versus a
          redemption:
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Discount Definition</div>
            <div className="dp-field-v">
              A reusable, tenant-scoped template &mdash; type (Percentage or Fixed), a value (per
              currency for Fixed), and a duration rule. It has no target and affects nothing on its
              own.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Discount Instance</div>
            <div className="dp-field-v">
              Terms applied to one target on one subscription &mdash; the subscription&rsquo;s
              base price, a specific Variable Component, or the whole invoice. An instance&rsquo;s terms can come
              from a library definition or be defined inline as a one-off (see below), but either
              way the instance itself is what actually reduces an invoice.
            </div>
          </div>
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Stopping a definition blocks it from being applied again but leaves existing instances
          untouched. Archiving is destructive to any still-active instance built from it &mdash;
          the dashboard runs a check and shows you exactly which subscriptions would lose their
          discount before you confirm.
        </p>
      </section>

      <section className="dp-section">
        <h2>Source: reusable vs. one-off</h2>
        <p className="dp-section-sub">
          Independent of duration &mdash; where an instance&rsquo;s terms come from when you apply
          it from a subscription&rsquo;s Discounts card, while creating a subscription, or by asking the{' '}
          <Link to="/docs/reference/assistant-tools">Revvly Assistant</Link>.
        </p>
        <div className="dp-fields-table">
          {SOURCES.map((s) => (
            <div className="dp-field-row" key={s.key}>
              <div className="dp-field-k">{s.name}</div>
              <div className="dp-field-v">{s.blurb}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Reach for a <strong>library</strong> discount when you expect to grant the same terms to
          more than one customer &mdash; a launch promo, a referral credit, a standard win-back
          offer &mdash; so it shows up in the tenant-wide analytics and expiring-discounts view as
          one named thing across every subscription it&rsquo;s applied to. Reach for a{' '}
          <strong>one-off</strong> when the terms are truly bespoke to this customer and you have
          no intention of reusing them &mdash; a support-escalation credit, a one-time
          goodwill gesture, a rate negotiated in a call that doesn&rsquo;t belong in the shared
          library. A one-off can still use any duration, including{' '}
          <code>FOREVER</code>; &ldquo;one-off&rdquo; describes where the terms are defined, not
          how long the discount lasts.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Either way, applying an instance to a subscription requires a reason, and both paths are
          cancellable independently &mdash; cancelling a one-off has no effect on anyone else,
          since nothing about it was ever shared.
        </p>
      </section>

      <section className="dp-section">
        <h2>Duration</h2>
        <p className="dp-section-sub">How long an instance keeps applying once it&rsquo;s attached to a subscription.</p>
        <div className="dp-fields-table">
          {DURATIONS.map((d) => (
            <div className="dp-field-row" key={d.key}>
              <div className="dp-field-k">{d.name}</div>
              <div className="dp-field-v">{d.blurb}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>Stacking</h2>
        <p className="dp-section-sub">
          What happens when more than one discount is active on the same target at once (for
          example two base-price discounts), set per-subscription via{' '}
          <code>discountStackingMode</code>. Discounts on different targets never compete: a
          base-price discount and a component discount both apply. A discount that loses under the
          stacking mode is not invoiced and does not use up any of its duration.
        </p>
        <div className="dp-fields-table">
          {STACKING.map((s) => (
            <div className="dp-field-row" key={s.key}>
              <div className="dp-field-k">{s.name}</div>
              <div className="dp-field-v">{s.blurb}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>Amount and options</h2>
        <p className="dp-section-sub">
          Every discount, whether from the library or one-off, can be shaped with these settings
          when you apply it.
        </p>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Percentage or fixed</div>
            <div className="dp-field-v">
              A percentage is a plain percent (10 means 10%) and works in any currency. A fixed
              amount is in the subscription&rsquo;s currency. A library discount with a fixed amount
              is only available where it defines a value for that currency; on any other currency
              it cannot be applied.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fixed mode (components)</div>
            <div className="dp-field-v">
              For a fixed amount on a component: <strong>per unit</strong> (the default) takes the
              amount off each unit&rsquo;s rate, so $2 off on 5 units is $10 off; <strong>flat lump
              sum</strong> takes the amount once off the whole line. A base-price discount has no
              units, so the setting does not apply there.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Unit limit (components)</div>
            <div className="dp-field-v">
              Discount at most this many of the billed units, for example 20% off 3 of 15 seats.
              Leave it empty to discount every unit. A limit at or above the billed quantity changes
              nothing.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">End date</div>
            <div className="dp-field-v">
              An optional calendar date. The discount stops applying to invoices whose service
              period starts on or after it, whichever comes first, the date or the duration.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Adjustment invoices</div>
            <div className="dp-field-v">
              Whether mid-cycle adjustment invoices are discounted too. On by default.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Reason</div>
            <div className="dp-field-v">
              Required. Recorded in the Discount History with who applied it.
            </div>
          </div>
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Discounts never take a line below zero: a fixed amount larger than what is left just
          consumes the rest. A discount takes effect from the next invoice generated; invoices
          already issued are not rewritten. You can later overwrite an active instance&rsquo;s value,
          duration or options on that subscription only, which leaves the library definition and
          other subscriptions alone, or cancel it, which is prospective.
        </p>
      </section>

      <section className="dp-section">
        <h2>Where it applies</h2>
        <p className="dp-plain">
          Invoice generation runs the normal pricing chain first &mdash;
          {' '}<Link to="/docs/guides/custom-agreements">Custom Pricing Agreement</Link> resolution,
          {' '}<Link to="/docs/guides/scoped-pricing">scope walk</Link>, included-quantity netting
          &mdash; and produces line items exactly as it would with no discounts at all. Only then
          are active discount instances applied, strictly last, against the target they were
          attached to (base price, one Variable Component, or the whole invoice). A whole-invoice discount is applied last of all, to every line after the base-price and component discounts have taken their share, one-time fees included. The base-price lines of a multi-module plan, and the per-tier lines of a graduated Variable Component, are each discounted as one target and the result is shared back across the lines, so a fixed amount comes off once, not once per line. The pre-discount amount and each
          discount&rsquo;s contribution are preserved on the invoice line so a customer&rsquo;s bill
          always shows what changed and why.
        </p>
        <Figure
          title="Order of discounting"
          caption="Discounts never touch the resolved rate; they apply after it, whole-invoice last."
        >
          <Flow
            steps={[
              { title: 'Pricing chain resolves', text: 'Agreement, scope walk, included-quantity netting.', tone: 'muted' },
              { title: 'Lines are built', text: 'Same lines as with no discounts at all.' },
              { title: 'Base and component discounts', text: 'Each target is discounted as one group across its lines.', tone: 'accent' },
              { title: 'Whole-invoice discount', text: 'Last of all, every line including one-time fees.', tone: 'warn' },
            ]}
          />
        </Figure>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          By default, a discount also reduces mid-cycle adjustment invoices &mdash; expansions and
          overage invoices. Turn off its <code>applyToAdjustments</code> flag to limit a discount to
          standard start and renewal invoices; adjustment invoices are then left at full price.
          Either way only start and renewal invoices use up a counted duration.
        </p>
      </section>

      <section className="dp-section">
        <h2>Lifecycle on renewal</h2>
        <p className="dp-plain">
          A counted instance (first invoice only, or a fixed number) only advances its applied-renewal counter, and only auto-expires,
          after the invoice it discounted has actually been generated and persisted &mdash; never
          speculatively. This keeps a failed or retried invoice run from burning down a
          customer&rsquo;s remaining discounted renewals. Every apply, cancel, expiry, and the
          renewal that consumes a count is written to an immutable Discount History log, separate
          from the general <Link to="/docs/concepts/lifecycleEvent">Lifecycle Event log</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>Previewing before you commit</h2>
        <p className="dp-plain">
          Because the math is a pure function of (resolved price, active instances, stacking mode),
          Revvly can show the effect of a discount before it is ever applied to a real invoice. The
          dashboard&rsquo;s discount preview walks the same calculation used at billing time and
          shows the pre-discount amount, each discount&rsquo;s deduction, and the final amount per
          target &mdash; so you can see next month&rsquo;s bill before saving anything.
        </p>
      </section>

      <section className="dp-section">
        <h2>Next up</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/custom-agreements" style={{ color: 'inherit', textDecoration: 'none' }}>
              Custom Pricing Agreements &rsaquo;
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/scoped-pricing" style={{ color: 'inherit', textDecoration: 'none' }}>
              Scope-based pricing &rsaquo;
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/subscription" style={{ color: 'inherit', textDecoration: 'none' }}>
              The Subscription concept &rsaquo;
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
